import type { CSSProperties } from 'react';

import { TEXT_COLOR_ROLES } from './tokens';

/** Strict 6-digit hex: the only form the SurfaceColorField editor widget emits. */
const COMPLETE_HEX = /^#[0-9a-fA-F]{6}$/;

/** Sentinel meaning "change nothing"; descendants keep whatever the page or theme provides. */
export const INHERIT_COLOR = 'inherit';

/**
 * Text color roles a container may push onto everything inside it.
 *
 * Deliberately excludes the `text` role: it resolves to `--dcm-color-text-primary`, which is the
 * very property this override writes, so choosing it would make the custom property reference
 * itself. A self-referential custom property is invalid at computed-value time, and the text would
 * silently fall back to `inherit` rather than the theme color.
 */
export const CONTAINER_TEXT_COLORS = {
  secondary: TEXT_COLOR_ROLES.secondary,
  muted: TEXT_COLOR_ROLES.muted,
  inverted: TEXT_COLOR_ROLES.inverted,
} as const;

export type ContainerTextColor = keyof typeof CONTAINER_TEXT_COLORS | typeof INHERIT_COLOR;

export const CONTAINER_TEXT_COLOR_OPTIONS = [
  { label: 'Inherit', value: INHERIT_COLOR },
  { label: 'Secondary', value: 'secondary' },
  { label: 'Muted', value: 'muted' },
  { label: 'Inverted', value: 'inverted' },
] as const;

/**
 * AI schema shared by every container text-color field.
 *
 * Deliberately *not* a closed enum: each consumer renders {@link ContainerTextColorField}, which
 * offers the named roles plus a custom hex escape hatch, so the AI plugin must be told a raw hex is
 * a legal value.
 *
 * One schema is only correct while every consumer can actually *display* a hex. If a container is
 * ever given a plain role-only `<select>` instead, it needs its own closed-enum schema — a hex
 * written into a `<select>` with no matching option renders correctly but leaves the author looking
 * at a blank control they can neither reproduce nor clear.
 */
export const CONTAINER_TEXT_COLOR_AI_SCHEMA = {
  type: 'string',
  description:
    "Either a named text color role ('inherit', 'secondary', 'muted', 'inverted'), or a custom hex value such as #ffffff. Prefer a role so the text follows merchant branding; use a hex only when a specific color is required.",
} as const;

/**
 * Builds the scoped `--dcm-*` override a container writes onto its own wrapper.
 *
 * Leaf components emit a `var()` *reference* rather than a resolved color — Heading sets
 * `--cms-heading-color: var(--dcm-color-text-primary, #485055)`. Redefining that property here
 * therefore re-points every descendant through the ordinary CSS cascade, with no changes to those
 * components at all. This is the mechanism a future named color-scheme feature reuses.
 *
 * Accepts either a named role key (from `CONTAINER_TEXT_COLORS`) or a valid 6-digit hex string.
 * A hex has no self-reference problem and is emitted verbatim as the custom property value.
 *
 * `textColor` is required, not `string | undefined`. It used to accept `undefined` for pages saved
 * before this control existed, on the theory that Puck doesn't merge `defaultProps` into the
 * production `<Render>` path. Confirmed with the product owner (ECOMM-12969) that no such page
 * exists for `Section`/`FlexLayout`, the only two callers.
 *
 * Still returns an empty object for `inherit`, an unrecognized persisted value, or a malformed hex
 * -- a value outside the offered set is a live, ordinary possibility (a hand-edit, an AI plugin, a
 * future removed option), unrelated to whether the key itself is present. Note there is no longer a
 * single early return for "not a known role": a hex is not a role key, so it must fall through to
 * the hex branch below rather than short-circuiting. A strict hex regex is used intentionally -- a
 * partial or otherwise invalid hex emitted verbatim would set the custom property, making the var()
 * fallback unreachable and silently breaking the cascade.
 *
 * DELIBERATE, ACCEPTED BEHAVIOR CHANGE for one narrow case: before hex support, this function
 * returned `{}` for a hex, so a hex sitting in saved page data rendered as no override at all. It
 * now applies. The editor could not produce such a value (the control was a role-only select and
 * the AI schema was a closed enum), so this only affects a page whose JSON was hand-edited or
 * written by a tool that ignored the enum. Where it does occur the new behavior is the intended
 * one — the stored color finally takes effect — but it IS a visual change to an already-published
 * page that nobody re-saved, so it is recorded here rather than left to be discovered.
 * `containerColorStyle` is called by both Section and FlexLayout, so both are in scope.
 */
export function containerColorStyle(textColor: string): CSSProperties {
  if (Object.prototype.hasOwnProperty.call(CONTAINER_TEXT_COLORS, textColor)) {
    return {
      '--dcm-color-text-primary': CONTAINER_TEXT_COLORS[textColor as keyof typeof CONTAINER_TEXT_COLORS],
    } as CSSProperties;
  }

  if (COMPLETE_HEX.test(textColor)) {
    return { '--dcm-color-text-primary': textColor } as CSSProperties;
  }

  return {};
}
